import React, { Fragment, useState } from 'react'

import { useHistory } from 'react-router-dom'
import { reverse } from 'named-urls'
import { Button, Chip } from '@material-ui/core'

import { AlertDialog, MenuButton, Permitted } from 'components'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from 'components/Table'

import useSnackbar from 'hooks/useSnackbar'

import helpers from 'helpers'
import theme from 'theme'
import { routes } from 'Routes'
import useStyles from './styles'
import * as service from 'service'
import constants from 'constants/index'

const TreatmentTable = ({ dataProcessTreatments, isLoading, refresh }) => {
  const [loading, setLoading] = useState(false)
  const [selectedTreatment, setSelectedTreatment] = useState(null)

  const {
    CHIP_STATUS_COLOR,
    CHIP_STATUS_LABEL,
    CHIP_STATUS_TEXT_COLOR,
    STATUS,
  } = constants.dataProcessTreatment

  const managePermissions = [
    constants.permissions.DATA_PROCESS_TREATMENT.MANAGE,
    constants.permissions.DATA_PROCESSES.CREATE,
  ]

  const history = useHistory()
  const snackbar = useSnackbar()
  const classes = useStyles()

  const handleSetTreatment = ({ ...params }) => setSelectedTreatment(params)

  const handleClearTreatment = () => setSelectedTreatment(null)

  const handleRedirect = (dataProcessTreatmentId) => {
    history.push(
      reverse(routes.dataProcess.treatment.manage, {
        dataProcessTreatmentId,
      }),
    )
  }

  const handleChangeStatus = async () => {
    try {
      setLoading(true)

      await service.dponet.dataProcessTreatment.changeStatus({
        dataProcessTreatmentId: selectedTreatment?.treatment?.id,
        status: selectedTreatment?.status,
      })

      snackbar.open({
        message: selectedTreatment?.success || 'Ação realizada com sucesso!',
        variant: 'success',
      })

      handleClearTreatment()
      refresh()
    } catch (error) {
      snackbar.open({
        message: helpers.formatters.errorMessage(error),
        variant: 'error',
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <Fragment>
      <Table
        isLoading={isLoading}
        emptyMessage="Nenhum tratamento de processo foi encontrado."
      >
        <TableHead>
          <TableRow>
            <TableCell width="40%">Processo</TableCell>
            <TableCell width="25%">Departamento</TableCell>
            <TableCell width="10%" align="center">
              Risco
            </TableCell>
            <TableCell width="10%" align="center">
              Status do Tratamento
            </TableCell>
            <TableCell width="10%" align="center">
              Prazo
            </TableCell>
            <Permitted someTags={managePermissions}>
              <TableCell width="5%" align="center">
                Ações
              </TableCell>
            </Permitted>
          </TableRow>
        </TableHead>
        <TableBody>
          {dataProcessTreatments?.map((treatment) => {
            const dataProcess = treatment?.dataProcess
            const chipColor = dataProcess?.dataProcessRisk?.color
            const chipTextColor = dataProcess?.dataProcessRisk?.secondaryColor
            const chipLabel = dataProcess?.dataProcessRisk?.label

            const isPending = treatment?.status === STATUS.PENDING_ID
            const isInProgress = treatment?.status === STATUS.IN_PROGRESS_ID
            const isOverdue = treatment?.status === STATUS.OVERDUE_ID

            const isOpen = isPending || isInProgress || isOverdue

            return (
              <TableRow key={treatment.id}>
                <TableCell width="40%">{`#${dataProcess?.id} ${dataProcess?.name}`}</TableCell>
                <TableCell width="25%">
                  {dataProcess?.department?.name}
                </TableCell>
                <TableCell width="10%" align="center">
                  <Chip
                    size="small"
                    label={chipLabel || 'Não definido'}
                    style={{
                      ...(chipColor && {
                        backgroundColor: chipColor,
                        color:
                          chipTextColor ||
                          theme.palette.getContrastText(chipColor),
                      }),
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
                <TableCell width="10%" align="center">
                  <Chip
                    size="small"
                    label={
                      CHIP_STATUS_LABEL[treatment?.status] || 'Não definido'
                    }
                    style={{
                      backgroundColor: CHIP_STATUS_COLOR[treatment?.status],
                      color: CHIP_STATUS_TEXT_COLOR[treatment?.status],
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
                <TableCell width="10%" align="center">
                  {helpers.formatters.dates.ptBrFormatDate(
                    treatment?.deadlineDate,
                  ) || 'Não definido'}
                </TableCell>
                <Permitted someTags={managePermissions}>
                  <TableCell width="5%" align="center">
                    <MenuButton iconButtonSize="small">
                      <Button
                        onClick={() => handleRedirect(treatment?.id)}
                        fullWidth
                      >
                        Gerenciar
                      </Button>
                      {isOpen ? (
                        <Button
                          disabled={loading}
                          onClick={() =>
                            handleSetTreatment({
                              treatment,
                              status: STATUS.RISK_ASSUMED_ID,
                              alert:
                                'Tem certeza que deseja assumir o risco deste tratamento?',
                              success: 'Risco assumido com sucesso!',
                              button: 'Assumir risco',
                            })
                          }
                          fullWidth
                        >
                          Assumir risco
                        </Button>
                      ) : (
                        <Button
                          disabled={loading}
                          onClick={() =>
                            handleSetTreatment({
                              treatment,
                              status: STATUS.IN_PROGRESS_ID,
                              alert:
                                'Tem certeza que deseja reabrir este tratamento?',
                              success: 'Tratamento reaberto com sucesso!',
                              button: 'Reabrir',
                            })
                          }
                          fullWidth
                        >
                          Reabrir
                        </Button>
                      )}
                      <Button
                        disabled={loading}
                        onClick={() =>
                          handleSetTreatment({
                            treatment,
                            status: STATUS.SOFT_DELETED_ID,
                            alert:
                              'Tem certeza que deseja remover este tratamento?',
                            success: 'Tratamento removido com sucesso!',
                            button: 'Remover',
                          })
                        }
                        fullWidth
                      >
                        Remover
                      </Button>
                    </MenuButton>
                  </TableCell>
                </Permitted>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      {!!selectedTreatment && (
        <AlertDialog
          open={!!selectedTreatment}
          onClose={handleClearTreatment}
          variant="outlined"
          severity="warning"
          content={selectedTreatment?.alert}
          loading={loading}
          dialogActions={
            <>
              <Button
                onClick={handleClearTreatment}
                variant="outlined"
                color="inherit"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleChangeStatus}
                className={classes.warningButton}
                disabled={loading}
              >
                {selectedTreatment?.button}
              </Button>
            </>
          }
        />
      )}
    </Fragment>
  )
}

export default TreatmentTable
